import { createContext, useContext } from 'react'
import type { CurrentUser } from '../types'
import type { Blocked, Catalog, FinanceReport, RunBody } from './data'
import type { DateRange, PeriodSelection } from './period'
import type { DrawerOrigin } from './Drawer'
import type { Coverage, PeriodGap } from './coverage'

export type SectionCode = 'overview' | 'pnl' | 'cash' | 'debts' | 'margin'

/** Что открыть в панели детализации. */
export type DrillTarget =
  | { kind: 'sales'; dimension: string; value: string; label: string; path?: Array<{ dimension: string; value: string; label: string }> }
  | { kind: 'customer'; value: string; label: string }
  | { kind: 'cash_item'; value: string; label: string }
  | { kind: 'debtor'; value: string; label: string }

export interface FinanceContextValue {
  user: CurrentUser
  catalog: Catalog
  selection: PeriodSelection
  range: DateRange
  previousRange: DateRange
  trend: DateRange
  /** GUID выбранного филиала или null — «все города». */
  branch: string | null
  branchName: string | null
  /** Что видно при «все города»: у ограниченного правами — только его города. */
  scopeLabel: string
  /** Права человека сужены по филиалам. */
  restricted: boolean
  /**
   * Тело запроса с фильтром филиала там, где он есть; null — отчёт недоступен;
   * Blocked — человек ограничен по городам, а отчёт по ним не делится:
   * сервер откажет, поэтому запрос не отправляется, а блок объясняет причину.
   */
  scoped: (code: FinanceReport, body: RunBody) => RunBody | Blocked | null
  /** Почему отчёт закрыт срезом прав (не делится по городам); null — не закрыт. */
  scopeBlocked: (code: FinanceReport) => string | null
  /** Филиал выбран, а отчёт по филиалам не делится. */
  branchIgnored: (code: FinanceReport) => boolean
  /** Фильтр «только дебиторская», если бэкенд уже различает стороны долга. */
  receivableFilter: Record<string, string[]>
  /**
   * Долги между своими организациями исключены из итогов настройкой клиента
   * (админка «Финансовая разметка»); итог показывает их отдельной строкой.
   */
  debtsExternalOnly?: boolean
  go: (section: SectionCode, params?: Record<string, string>) => void
  drill: (target: DrillTarget, origin: DrawerOrigin) => void
  /** За какие месяцы в выгрузке есть продажи: ноль и «не загружено» — разное. */
  coverage: Coverage
  /** Продаж за выбранный период нет совсем — почему и куда перейти. */
  gap: PeriodGap | null
  /** Период загружен частично — какие месяцы не входят в итог. */
  partial: string | null
  /** Почему сравнение с прошлым периодом не показывается; null — показывается. */
  noCompare: string | null
  /** Сменить период (кнопка «Показать июнь 2026» в пустом блоке). */
  setSelection: (selection: PeriodSelection) => void
}

export const FinanceContext = createContext<FinanceContextValue | null>(null)

export function useFinance(): FinanceContextValue {
  const value = useContext(FinanceContext)
  if (!value) throw new Error('useFinance вне FinanceContext')
  return value
}
